import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { Globe } from "Shared/appIcons";
import { BrandMark } from "Shared/Layouts/components/BrandMark";
import { ModuleLayoutToggle } from "Shared/Layouts/components/ModuleLayoutToggle";
import type { NavItem } from "Shared/Layouts/components/NavButton";
import { ThemeToggle } from "Shared/Layouts/components/ThemeToggle";
import { HorizontalNavButton } from "Shared/Layouts/components/TopbarNav/HorizontalNavButton";
import { UserMenu } from "Shared/Layouts/components/TopbarNav/UserMenu";
import { SimpleTooltip } from "Shared/SimpleTooltip";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";

type Props = {
	navItems: NavItem[];
	isActive: (to?: string) => boolean;
	userLogin: string;
	userName: string;
	tenantName: string;
	userInitials: string;
	onSignOut: () => void;
};

/**
 * Horizontal top navigation: brand · nav items · theme/lang · user menu.
 * Hidden below the `md` breakpoint — `DrawerNav` takes over there.
 */
export const TopbarNav = ({
	navItems,
	isActive,
	userLogin,
	userName,
	tenantName,
	userInitials,
	onSignOut,
}: Props) => {
	const { t } = useResource();
	const top = navItems.filter((i) => i.group === "top");
	const bottom = navItems.filter((i) => i.group === "bottom");
	const hasBottom = bottom.length > 0;

	return (
		<header className="border-border bg-card hidden h-14 flex-shrink-0 items-center gap-6 border-b px-6 md:flex">
			<SimpleTooltip content={t(Resources.Sidebar.BrandTitle)} delay={0}>
				<Link
					to={AppRouting.Dashboard.path()}
					className="group flex flex-shrink-0 items-center gap-2.5 no-underline"
				>
					<BrandMark textClassName="hidden text-center lg:flex" />
				</Link>
			</SimpleTooltip>

			<div className="bg-border hidden h-5 w-px lg:block" />

			<nav className="flex min-w-0 flex-shrink items-center gap-1">
				{top.map((item) => (
					<HorizontalNavButton
						key={item.key}
						item={item}
						active={isActive(item.to)}
					/>
				))}
				{hasBottom ? (
					<>
						<div className="bg-border mx-1.5 h-5 w-px" />
						{bottom.map((item) => (
							<HorizontalNavButton
								key={item.key}
								item={item}
								active={isActive(item.to)}
							/>
						))}
					</>
				) : null}
			</nav>

			<div className="flex-1" />

			<div className="flex flex-shrink-0 items-center gap-2">
				<ModuleLayoutToggle />
				<ThemeToggle />
				<SimpleTooltip content={t(Resources.Sidebar.LanguageSwitcher)}>
					<Button
						variant="ghost"
						size="icon-sm"
						aria-label={t(Resources.Sidebar.LanguageSwitcher)}
						className="text-muted-foreground hover:text-foreground rounded-full"
					>
						<Globe className="h-4 w-4" />
					</Button>
				</SimpleTooltip>
				<UserMenu
					userLogin={userLogin}
					userName={userName}
					tenantName={tenantName}
					userInitials={userInitials}
					onSignOut={onSignOut}
				/>
			</div>
		</header>
	);
};
